import React, { useCallback, useState } from "react";
import {
  Row,
  Label,
  Input,
  Form,
  FormGroup,
  FormFeedback,
  Col,
  Button,
} from "reactstrap";
import { useFormik } from "formik";
import * as yup from "yup";
//Import Flatepicker
import "flatpickr/dist/themes/material_blue.css";
import { useDropzone } from "react-dropzone";
import { splitFileIntoChunksBase64 } from "shared/fileUtils";
import { BiLoaderAlt, BiCheckCircle } from "react-icons/bi";

import {
  completeUpload,
  uploadChunk,
} from "../../../helpers/AuthType/authBackend";

const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB
interface BasicInformationProps {
  onSubmit: (values: any) => void;
  onBack: () => void;
  data?: any;
  userId: any;
}

const AddressVerification = ({
  onSubmit,
  onBack,
  data,
  userId,
}: BasicInformationProps) => {
  //meta title
  document.title = "Ditobanx";

  const [selectedFiles, setSelectedFiles] = useState<any[]>([]);
  // const [base64Recibo, setBase64Recibo] = useState<any>(null);
  // --- estados ---
  const [loadingRecibo, setLoadingRecibo] = useState(false);
  const [reciboValidated, setReciboValidated] = useState(false);

  // Función para el documento (permite varios archivos)
  const onDropDocs = useCallback((acceptedFiles: any[]) => {
    const validFiles = acceptedFiles.filter((file) => {
      if (file.size > MAX_FILE_SIZE) {
        alert(
          `El archivo ${file.name} supera el tamaño máximo permitido (2 MB).`
        );
        return false;
      }
      return true;
    });

    const mappedFiles = validFiles.map((file) =>
      Object.assign(file, {
        preview: URL.createObjectURL(file),
      })
    );
    // Se agregan al estado sin eliminar los anteriores
    setSelectedFiles((prevFiles) => [...prevFiles, ...mappedFiles]);
  }, []);

  // Dropzone para documentos
  const { getInputProps: getDocInputProps, open: openDoc } = useDropzone({
    onDrop: onDropDocs,
    noClick: true,
    noKeyboard: true,
    accept: {
      "image/*": [],
    },
    maxFiles: 2,
  });

  // Función para eliminar un documento por índice
  const removeDoc = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    setReciboValidated(false); // vuelve a requerir validación
    setLoadingRecibo(false); // por si estaba en curso
  };

  // CONVERTIR A BASE64
  // const fileToBase64 = (file: File): Promise<string> => {
  //   return new Promise((resolve, reject) => {
  //     const reader = new FileReader();

  //     reader.onloadend = () => {
  //       resolve(reader.result as string);
  //     };

  //     reader.onerror = () => {
  //       reject(new Error("Error al convertir el archivo a base64"));
  //     };

  //     reader.readAsDataURL(file);
  //   });
  // };

  const uploadFileChunks = async (file: File, type: string) => {
    const chunks = await splitFileIntoChunksBase64(file);
    const uploadId = `${file.name}-${Date.now()}`;
    const totalChunks = chunks.length;

    for (let i = 0; i < totalChunks; i++) {
      const payload = {
        uploadId,
        filename: file.name,
        chunkIndex: i,
        totalChunks,
        chunkBase64: chunks[i],
        type, // Ej: "selfie", "front", etc.
      };

      await uploadChunk(payload);
    }

    // Notificamos que se terminó de subir
    await completeUpload({
      uploadId,
      filename: file.name,
      type,
      userId,
    });

    return uploadId;
  };

  const validarPhotoUploaded = async (type: string) => {
    if (type !== "recibo") return;

    try {
      setLoadingRecibo(true); // ⏳ spinner
      await uploadFileChunks(selectedFiles[0], "recibo");
      setReciboValidated(true); // ✅ validado
    } catch (err) {
      console.error("Error:", err);
      alert("Ocurrió un error al subir el recibo.");
    } finally {
      setLoadingRecibo(false); // 🔚 detiene spinner
    }
  };

  const formik: any = useFormik({
    initialValues: {
      address: (data.addressInfo && data.addressInfo.address) || "",
      city: (data.addressInfo && data.addressInfo.city) || "",
      postal_code: (data.addressInfo && data.addressInfo.postal_code) || "",
      province_state:
        (data.addressInfo && data.addressInfo.province_state) || "",
    },
    validationSchema: yup.object().shape({
      address: yup.string().required("Please Enter Your address"),
      city: yup.string().required("Please Enter Your City"),
      postal_code: yup.string().required("Please Enter Your postal code"),
      province_state: yup.string().required("Please Enter Your Province"),
    }),
    onSubmit: (values: any) => {
      // console.log("values", values);
      if (reciboValidated) {
        onSubmit(values); // tu callback real
      } else {
        alert("Por favor verifica que el recibo ha sido validado.");
      }
    },
  });

  return (
    <React.Fragment>
      <Form onSubmit={formik.handleSubmit} autoComplete="off">
        <Row>
          {/* ADDRESS */}
          <FormGroup className="mb-3">
            <Label htmlFor="address">Dirección</Label>
            <Input
              type="text"
              id="address"
              placeholder="ingresa tu Dirección"
              name="address"
              value={formik.values.address || ""}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              invalid={
                formik.touched.address && formik.errors.address ? true : false
              }
            />
            {formik.touched.address && formik.errors.address ? (
              <FormFeedback type="invalid">
                {formik.errors.address}
              </FormFeedback>
            ) : null}
          </FormGroup>
          <Col lg={6}>
            {/* CITY */}
            <FormGroup className="mb-3">
              <Label htmlFor="city">Ciudad</Label>
              <Input
                type="text"
                id="city"
                placeholder="ingresa tu ciudad"
                name="city"
                value={formik.values.city || ""}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                invalid={
                  formik.touched.city && formik.errors.city ? true : false
                }
              />
              {formik.touched.city && formik.errors.city ? (
                <FormFeedback type="invalid">{formik.errors.city}</FormFeedback>
              ) : null}
            </FormGroup>
            {/* POSTAL_CODE */}
            <FormGroup className="mb-3">
              <Label htmlFor="postal_code">Codigo postal </Label>
              <Input
                type="text"
                className="form-control"
                name="postal_code"
                id="postal_code"
                value={formik.values.postal_code || ""}
                // defaultValue={"11040"}
                onChange={formik.handleChange}
                placeholder="Ingresa el codigo postal"
                onBlur={formik.handleBlur}
              />
              {formik.touched.postal_code && formik.errors.postal_code ? (
                <span className="text-danger">{formik.errors.postal_code}</span>
              ) : null}
            </FormGroup>
          </Col>
          {/* PROVINCIA/ESTADO */}
          <Col lg={6}>
            <FormGroup className="mb-3">
              <Label htmlFor="province_state">Provincia/Estado </Label>
              <Input
                type="text"
                className="form-control"
                name="province_state"
                id="province_state"
                value={formik.values.province_state || ""}
                onChange={formik.handleChange}
                placeholder="Ingresa el codigo postal"
                onBlur={formik.handleBlur}
              />
              {formik.touched.province_state && formik.errors.province_state ? (
                <span className="text-danger">
                  {formik.errors.province_state}
                </span>
              ) : null}
            </FormGroup>
          </Col>
          {/* FOTO DOCUMENTO IDENTIDAD */}
          <FormGroup className="mb-3">
            <Label htmlFor="documento-input">Comprobante de Domicilio</Label>
            <p>Una vez cargado el documento debes darle verificar</p>
            <div
              style={{
                textAlign: "center",
                padding: "20px",
                justifyContent: "center",
                alignItems: "center",
                flexDirection: "column",
                border: "1px solid #ccc",
                borderRadius: "8px",
                backgroundColor: "#f9f9f9",
              }}
            >
              <i className="bx bx-upload" style={{ fontSize: "40px" }}></i>
              <h5 className="font-size-14 mb-3">
                Selecciona una imagen, tamaño maximo de 2MB
              </h5>
              {/* Input controlado por Dropzone */}
              <input {...getDocInputProps()} />
              <Button
                type="button"
                color="primary"
                disabled={selectedFiles.length >= 1}
                onClick={(e) => {
                  e.preventDefault();
                  openDoc();
                }}
              >
                Subir Archivo
              </Button>

              {/* Previsualización para documento */}
              <div
                style={{
                  marginTop: "20px",
                  display: "flex",
                  flexWrap: "wrap",
                  justifyContent: "center",
                }}
              >
                {selectedFiles.map((file, index) => (
                  <div
                    key={index}
                    style={{ position: "relative", marginBottom: "10px" }}
                  >
                    {/* Botón para eliminar */}
                    <button
                      type="button"
                      onClick={() => removeDoc(index)}
                      style={{
                        position: "absolute",
                        top: "5px",
                        right: "5px",
                        backgroundColor: "red",
                        color: "white",
                        border: "none",
                        borderRadius: "50%",
                        width: "25px",
                        height: "25px",
                        cursor: "pointer",
                      }}
                    >
                      &times;
                    </button>
                    {/* {!reciboValidated ? (
                    ) : null} */}
                    <img
                      src={file.preview}
                      alt={file.name}
                      style={{
                        maxWidth: "100%",
                        height: "200px",
                        borderRadius: "8px",
                      }}
                    />
                    <p>{file.name}</p>
                  </div>
                ))}
              </div>
              {selectedFiles.length > 0 && (
                <Button
                  type="button"
                  color={reciboValidated ? "success" : "primary"}
                  disabled={reciboValidated || loadingRecibo}
                  onClick={(e) => {
                    e.preventDefault();
                    validarPhotoUploaded("recibo");
                  }}
                  className="flex items-center gap-2"
                >
                  {loadingRecibo && <BiLoaderAlt className="animate-spin" />}
                  {reciboValidated && !loadingRecibo && (
                    <BiCheckCircle className="text-green-500" />
                  )}
                  {loadingRecibo
                    ? "Cargando…"
                    : reciboValidated
                    ? "Validado"
                    : "Verificar"}
                </Button>
              )}
            </div>
          </FormGroup>
        </Row>
        <div
          style={{
            marginTop: "20px",
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          {/* <button
            type="button"
            className="btn btn-secondary me-2"
            onClick={onBack}
          >
            Regresar
          </button> */}
          <Button type="submit" className="btn btn-primary">
            Siguiente
          </Button>
        </div>
      </Form>
    </React.Fragment>
  );
};

export default AddressVerification;
